import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Tabs } from 'antd';
import { AlertOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  createAlertRule,
  listAlertRules,
  listAlerts,
  setAlertRuleEnabled,
  type AlertRuleRequest,
  type AlertRuleView,
  type AlertView,
} from '@/api/alerts';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { usePageTitle } from '@/components/pageTitle';
import { AlertRegister, type AlertRegisterFilters } from '@/alerts/AlertRegister';
import { AlertDetailDrawer } from '@/alerts/AlertDetailDrawer';
import { RulesPanel } from '@/alerts/RulesPanel';
import { RuleFormModal } from '@/alerts/RuleFormModal';
import { SignalsIngressCard } from '@/alerts/SignalsIngressCard';
import { SignalTypesPanel } from '@/alerts/SignalTypesPanel';
import { TestSignalModal } from '@/alerts/TestSignalModal';
import { useAuth } from '@/auth/OlAuthContext';
import { getPermissionMatrix } from '@/admin/permissionMatrix';

/**
 * /alerts (OPSFL-ALUI-1) — the alert centre's console surface over the S7 backend: the durable
 * register (GET /v1/alerts, ACL-narrowed like the workbench queue), the routing-rule catalogue
 * (GET/POST /v1/alerts/rules + enable/disable) and the intake explainer. The register LINKS INTO
 * the Workbench — an alert is WORKED on its routed work item there; here you see state, routing
 * outcomes (failures loud, I4) and the disposition trail.
 */
export function AlertsPage() {
  usePageTitle('Alerts');
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [filters, setFilters] = useState<AlertRegisterFilters>({});
  const [alerts, setAlerts] = useState<AlertView[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [knownTypes, setKnownTypes] = useState<string[]>([]);
  const [openAlert, setOpenAlert] = useState<AlertView | null>(null);

  const [rules, setRules] = useState<AlertRuleView[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesDenied, setRulesDenied] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  /** "Duplicate as replacement" source — non-null opens the create modal prefilled from it. */
  const [duplicateSource, setDuplicateSource] = useState<AlertRuleView | null>(null);
  const [testSignalOpen, setTestSignalOpen] = useState(false);
  const [togglingIds, setTogglingIds] = useState<ReadonlySet<string>>(new Set());

  /**
   * Visual gate for the test-fire action AND the signal-type writes (`alerts.rules.write` —
   * reused, no new key). The SideNav convention: roles UNKNOWN → fail open (the API enforces,
   * deny-closed, regardless).
   */
  const canTestFire = useMemo(() => {
    if (!user || !user.rolesKnown) return true;
    try {
      const writeRoles =
        getPermissionMatrix().keys.find((k) => k.key === 'alerts.rules.write')?.roles ?? [];
      return writeRoles.some((r) => user.roles.includes(r));
    } catch {
      return true; // matrix drift renders loud in the IAM console; the API still enforces
    }
  }, [user]);

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const view = await listAlerts(filters);
      setAlerts(view.alerts);
      setKnownTypes((prev) =>
        [...new Set([...prev, ...view.alerts.map((a) => a.type)])].sort(),
      );
      setUnavailable(false);
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
    // message is stable (App.useApp)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const view = await listAlertRules();
      setRules(view.rules);
      setRulesDenied(false);
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.httpStatus === 403) {
        setRulesDenied(true); // honest: the read key is narrower than alerts.read
      } else if (!isNotAvailable(n)) {
        message.error(n.message);
      }
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const onCreateRule = useCallback(
    async (request: AlertRuleRequest) => {
      try {
        const created = await createAlertRule(request);
        message.success(`Rule '${created.name}' created.`);
        void loadRules();
        // The second explicit act of "duplicate as replacement" (OPSFL-FIX-1): offer — never
        // auto-perform — a one-click disable of the original. Both acts are recorded server-side
        // (created_by on the replacement, updated_by on the disable).
        const source = duplicateSource;
        if (source && source.enabled) {
          modal.confirm({
            title: `Replacement created — disable the original '${source.name}'?`,
            content:
              'Disabling stops the original matching new alerts; its history and past routings ' +
              'stay untouched. Keep both enabled only if you want both to match.',
            okText: 'Disable original',
            cancelText: 'Keep both enabled',
            onOk: async () => {
              try {
                const updated = await setAlertRuleEnabled(source.ruleId, false);
                setRules((prev) =>
                  prev.map((r) => (r.ruleId === updated.ruleId ? updated : r)),
                );
                message.success(`Original rule '${source.name}' disabled.`);
              } catch (e) {
                message.error(normalizeApiError(e).message);
              }
            },
          });
        }
      } catch (e) {
        // Re-thrown with the API's own message — the modal shows it verbatim.
        throw new Error(normalizeApiError(e).message);
      }
    },
    [duplicateSource, loadRules, message, modal],
  );

  const onToggleRule = useCallback(
    (rule: AlertRuleView, enabled: boolean) => {
      setTogglingIds((prev) => new Set(prev).add(rule.ruleId));
      setAlertRuleEnabled(rule.ruleId, enabled)
        .then((updated) => {
          setRules((prev) => prev.map((r) => (r.ruleId === updated.ruleId ? updated : r)));
          message.success(`Rule '${rule.name}' ${enabled ? 'enabled' : 'disabled'}.`);
        })
        .catch((e) => message.error(normalizeApiError(e).message))
        .finally(() =>
          setTogglingIds((prev) => {
            const next = new Set(prev);
            next.delete(rule.ruleId);
            return next;
          }),
        );
    },
    [message],
  );

  const openCount = useMemo(() => alerts.filter((a) => a.status === 'OPEN').length, [alerts]);

  if (unavailable) {
    return (
      <div className="ol-page">
        <EmptyState
          icon={<AlertOutlined />}
          title="Alert centre"
          description="One durable register for everything that needs attention — the platform's own derived conditions plus signals posted by external systems — with routing rules that turn alerts into governed casework. This deployment does not expose the alerts API yet."
          bullets={[
            'A filterable register: severity, status, type, alert key — with the routed work item one click away',
            'Routing rules: flat field=value conditions, first match by priority starts or signals a published flow',
            'Routing failures are shown loud on the alert, never silently dropped',
            'External intake via the ratified HMAC-signed POST /api/v1/signals rail',
          ]}
        />
      </div>
    );
  }

  return (
    <div className="ol-page">
      <PageHeader
        title="Alerts"
        icon={<AlertOutlined />}
        subtitle={
          <>
            The hub&rsquo;s durable alert register and the rules that route it. An alert is worked
            in the Workbench on its routed work item — this register shows state, routing outcomes
            and the disposition trail. {openCount > 0 ? `${openCount} open on this page.` : ''}
          </>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              void loadAlerts();
              void loadRules();
            }}
            loading={alertsLoading}
          >
            Refresh
          </Button>
        }
      />

      <Tabs
        style={{ marginTop: -6 }}
        items={[
          {
            key: 'register',
            label: 'Register',
            children: (
              <AlertRegister
                alerts={alerts}
                loading={alertsLoading}
                filters={filters}
                onFiltersChange={setFilters}
                typeOptions={knownTypes}
                onOpen={setOpenAlert}
              />
            ),
          },
          {
            key: 'rules',
            label: 'Routing rules',
            children: (
              <RulesPanel
                rules={rules}
                loading={rulesLoading}
                readDenied={rulesDenied}
                onNewRule={() => setRuleModalOpen(true)}
                onDuplicate={setDuplicateSource}
                onToggle={onToggleRule}
                togglingIds={togglingIds}
              />
            ),
          },
          {
            key: 'intake',
            label: 'How alerts get in',
            children: (
              <>
                <SignalsIngressCard
                  canTestFire={canTestFire}
                  onTestSignal={() => setTestSignalOpen(true)}
                />
                {/* OPSFL-FLOWOPS-1 Track B: the hub-authorable catalogue the rail above gates on.
                    Same visual gate as the test-fire (alerts.rules.write, reused). */}
                <SignalTypesPanel canWrite={canTestFire} />
              </>
            ),
          },
        ]}
      />

      <AlertDetailDrawer
        alert={openAlert}
        onClose={() => setOpenAlert(null)}
        onOpenWorkItem={(id) => navigate(`/workbench?item=${encodeURIComponent(id)}`)}
        onOpenEntity={(id) => navigate(`/entities/${encodeURIComponent(id)}`)}
      />

      <RuleFormModal
        key={duplicateSource ? `dup-${duplicateSource.ruleId}` : 'new'}
        open={ruleModalOpen || duplicateSource !== null}
        prefillFrom={duplicateSource}
        onClose={() => {
          setRuleModalOpen(false);
          setDuplicateSource(null);
        }}
        onCreate={onCreateRule}
      />

      <TestSignalModal
        open={testSignalOpen}
        onClose={() => setTestSignalOpen(false)}
        onFired={() => void loadAlerts()}
        onOpenAlert={(result) => setOpenAlert(result.alert)}
      />
    </div>
  );
}
